/**
 * React Query hooks for notification API
 * Provides caching, background refetching, and error handling for notification data
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationApi, notificationApiKeys } from '../services/notificationApi';
import { useAuthStore } from '../store/authStore';

// SECURITY: Import user-aware cache utilities for secure cache isolation
import { useUserContext } from './useUserContext';
import { UserAwareCacheKeys } from '../utils/userAwareCacheKeys';

/**
 * Hook to fetch stored notifications with React Query
 */
export const useStoredNotifications = (options?: {
  limit?: number;
  offset?: number;
  unread_only?: boolean;
  event_types?: string[];
  since?: Date;
  enabled?: boolean;
}) => {
  const { isApiAuthReady } = useAuthStore();
  
  // SECURITY: Get user context for secure cache isolation
  const { userId, isReady: isUserContextReady } = useUserContext();
  
  return useQuery({
    queryKey: userId ? UserAwareCacheKeys.notifications(userId, options) : ['disabled'],
    queryFn: () => notificationApi.getNotifications(options),
    enabled: isApiAuthReady && isUserContextReady && !!userId && (options?.enabled ?? true),
    staleTime: 1000 * 60 * 5, // Consider data stale after 5 minutes (increased from 2 minutes)
    gcTime: 1000 * 60 * 15, // Keep in cache for 15 minutes (increased from 10 minutes)
    refetchOnWindowFocus: false, // 🔥 FIXED: Disable auto-refetch to prevent duplicates (manual sync handles this)
    refetchOnReconnect: false, // 🔥 FIXED: Disable reconnect refetch to reduce calls
    retry: (failureCount, error: any) => {
      // Don't retry on auth errors
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        return false;
      }
      return failureCount < 3;
    }
  });
};

/**
 * Hook to fetch unread notification count
 */
export const useUnreadNotificationCount = (daysBack: number = 7) => {
  const { isApiAuthReady } = useAuthStore();
  
  // SECURITY: Get user context for secure cache isolation
  const { userId, isReady: isUserContextReady } = useUserContext();
  
  return useQuery({
    queryKey: userId ? UserAwareCacheKeys.notificationUnreadCount(userId) : ['disabled'],
    queryFn: () => notificationApi.getUnreadCount(daysBack),
    enabled: isApiAuthReady && isUserContextReady && !!userId,
    staleTime: 1000 * 30, // Consider stale after 30 seconds (reduced from 5 minutes for faster updates)
    gcTime: 1000 * 60 * 10, // Keep in cache for 10 minutes
    refetchInterval: 1000 * 60 * 2, // 🔥 IMPROVED: Reduce to 2 minutes for better responsiveness when returning to browser
    refetchOnWindowFocus: true, // 🔥 IMPROVED: Enable to get immediate updates when returning to browser
    refetchOnReconnect: true, // 🔥 IMPROVED: Enable for better reconnection handling
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        return false;
      }
      return failureCount < 2;
    }
  });
};

/**
 * Hook to fetch notification statistics
 */
export const useNotificationStats = (daysBack: number = 30, enabled: boolean = true) => {
  const { isApiAuthReady } = useAuthStore();
  
  return useQuery({
    queryKey: notificationApiKeys.stats(daysBack),
    queryFn: () => notificationApi.getNotificationStats(daysBack),
    enabled: isApiAuthReady && enabled,
    staleTime: 1000 * 60 * 5, // Consider stale after 5 minutes
    gcTime: 1000 * 60 * 15, // Keep in cache for 15 minutes
    refetchOnWindowFocus: false, // Stats don't need frequent updates
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        return false;
      }
      return failureCount < 2;
    }
  });
};

/**
 * Hook to mark notification as read
 */
export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();
  const { userId } = useUserContext();
  
  return useMutation({
    mutationFn: (notificationId: string) => notificationApi.markAsRead(notificationId),
    onSuccess: () => {
      // SECURITY: Invalidate user-aware notification cache
      if (userId) {
        queryClient.invalidateQueries({ queryKey: UserAwareCacheKeys.notifications(userId) });
        queryClient.invalidateQueries({ queryKey: UserAwareCacheKeys.notificationUnreadCount(userId) });
      }
    },
    onError: (error: any) => {
      console.error('Failed to mark notification as read:', error);
    }
  });
};

/**
 * Hook to manually refetch all notification data with user-aware cache keys
 */
export const useRefreshNotifications = () => {
  const queryClient = useQueryClient();
  const { userId } = useUserContext();
  
  return {
    refreshAll: () => {
      if (!userId) return Promise.resolve();
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: UserAwareCacheKeys.notifications(userId) }),
        queryClient.invalidateQueries({ queryKey: UserAwareCacheKeys.notificationUnreadCount(userId) })
      ]);
    },
    refreshNotifications: () => {
      if (!userId) return Promise.resolve();
      return queryClient.invalidateQueries({ queryKey: UserAwareCacheKeys.notifications(userId) });
    },
    refreshUnreadCount: () => {
      if (!userId) return Promise.resolve();
      return queryClient.invalidateQueries({ queryKey: UserAwareCacheKeys.notificationUnreadCount(userId)
      });
    }
  };
};

/**
 * Hook to mark all notifications as read (persistent API call)
 */
export const useMarkAllNotificationsRead = () => {
  const queryClient = useQueryClient();
  const { userId } = useUserContext();
  
  return useMutation({
    mutationFn: (daysBack: number = 7) => notificationApi.markAllAsRead(daysBack),
    onSuccess: (data: any) => {
      console.log(`✅ [Hook] Marked ${data.marked_count} notifications as read`);
      // SECURITY: Invalidate user-aware notification cache
      if (userId) {
        queryClient.invalidateQueries({ queryKey: UserAwareCacheKeys.notifications(userId) });
        queryClient.invalidateQueries({ queryKey: UserAwareCacheKeys.notificationUnreadCount(userId) });
      }
    },
    onError: (error: any) => {
      console.error('Failed to mark all notifications as read:', error);
    }
  });
};